import { dec, QTY_MAX_DECIMALS, QTY_MAX_INT_DIGITS, qty, toQtyString } from '@app/shared';

/** Итог разбора введённого текста: значение либо причина отказа. Пустое поле — не ошибка. */
export interface ParsedInput<T> {
  value: T | null;
  error: string | null;
}

/** Больше — почти наверняка опечатка (лишние нули), а количество должно умещаться в целое БД. */
const MAX_INT = 1_000_000;

/**
 * Целое число (коробки, штук в коробке). Неверный ввод не исправляется: «1,5» не становится 15, а
 * «0» — единицей. Поле остаётся с тем, что ввели, наружу уходит «пусто», владелец видит причину.
 */
export function parseIntInput(raw: string, min: number): ParsedInput<number> {
  const text = raw.trim();
  if (text === '') return { value: null, error: null };
  if (/^-\d+([.,]\d+)?$/.test(text)) return { value: null, error: min > 0 ? `Число должно быть не меньше ${min}` : 'Число не может быть отрицательным' };
  if (!/^\d+$/.test(text)) return { value: null, error: 'Нужно целое число, без дробной части' };
  const value = parseInt(text, 10);
  if (value < min) return { value: null, error: `Число должно быть не меньше ${min}` };
  if (value > MAX_INT) return { value: null, error: 'Слишком большое число' };
  return { value, error: null };
}

/** Денежная сумма: неотрицательная, до двух знаков; запятая — то же, что точка (§1). */
export function parseMoneyInput(raw: string): ParsedInput<string> {
  const text = raw.trim().replace(',', '.');
  if (text === '') return { value: null, error: null };
  if (/^-\d*\.?\d*$/.test(text)) return { value: null, error: 'Сумма не может быть отрицательной' };
  if (/^\d+\.\d{3,}$/.test(text)) return { value: null, error: 'Не больше двух знаков после запятой' };
  if (!/^(\d{1,12}(\.\d{0,2})?|\.\d{1,2})$/.test(text)) return { value: null, error: 'Нужно число: например, 1250 или 1250,50' };
  return { value: dec(text.startsWith('.') ? `0${text}` : text).toFixed(2), error: null };
}

export interface QtyParseOptions {
  /** Знаков после запятой: по умолчанию столько, сколько сейчас принимает сервер (`QTY_MAX_DECIMALS`). */
  decimals?: number;
  /** Строго больше нуля («штук в коробке»); без флага допустим и 0 (факт россыпи при инвентаризации). */
  positive?: boolean;
  /** Верхняя граница включительно, в штуках. */
  max?: number;
}

const DECIMALS_WORD: Record<number, string> = { 1: 'одного знака', 2: 'двух знаков', 3: 'трёх знаков' };

/**
 * Количество в штуках: неотрицательное, не больше `decimals` знаков после запятой; запятая — то же,
 * что точка. Лишний знак — отказ, а не округление («4,3001» не становится 4,3). Возвращает каноническую
 * строку («4.3», «10»), в том же виде, в каком её отдаёт сервер, — через неё не проходит float.
 */
export function parseQtyInput(raw: string, options: QtyParseOptions = {}): ParsedInput<string> {
  const decimals = options.decimals ?? QTY_MAX_DECIMALS;
  const text = raw.trim().replace(',', '.');
  if (text === '') return { value: null, error: null };
  if (/^-\d*\.?\d*$/.test(text)) return { value: null, error: 'Количество не может быть отрицательным' };
  if (decimals === 0 && /^\d*\.\d*$/.test(text)) return { value: null, error: 'Нужно целое число, без дробной части' };
  if (decimals > 0 && new RegExp(`^\\d*\\.\\d{${decimals + 1},}$`).test(text)) {
    return { value: null, error: `Не больше ${DECIMALS_WORD[decimals] ?? `${decimals} знаков`} после запятой` };
  }
  const shape = decimals > 0 ? new RegExp(`^(\\d{1,${QTY_MAX_INT_DIGITS}}(\\.\\d{0,${decimals}})?|\\.\\d{1,${decimals}})$`) : new RegExp(`^\\d{1,${QTY_MAX_INT_DIGITS}}$`);
  if (!shape.test(text)) return { value: null, error: decimals > 0 ? 'Нужно число: например, 4 или 4,3' : 'Нужно целое число' };
  const value = qty(text.startsWith('.') ? `0${text}` : text.endsWith('.') ? text.slice(0, -1) : text);
  if (options.positive && !value.gt(0)) return { value: null, error: 'Число должно быть больше нуля' };
  if (options.max !== undefined && value.gt(options.max)) return { value: null, error: 'Слишком большое число' };
  return { value: toQtyString(value), error: null };
}
